import { GetWebsiteEventsResponse, Milestone, Primitive } from '@amzn/swo-unified-dashboard-service-lambda-js-client/lib/smswounifieddashboardservicelambda';
import { TimeMarker, ProcessedMilestone, Pipeline, Step, MergedPipeline } from '../types';
import { LAYOUT_CONSTANTS } from './constants';

// Predefined colors for milestones and primitives
export const milestoneColors = ['#607d8b'];
/**
 * Processes milestones into a format suitable for timeline display.
 * Calculates positions as percentages within the given time range and assigns colors.
 * This is a fallback function in case there are no pipelines to process
 * 
 * @param milestones - Array of milestone objects with timestamp properties
 * @param paddedStart - Start date of the padded time range
 * @param paddedEnd - End date of the padded time range
 * @returns Array of processed milestones with calculated positions and colors
 */
export const processMilestones = (
  milestones: any[], 
  paddedStart: Date, 
  paddedEnd: Date
): ProcessedMilestone[] => {
  const milestoneDates = milestones.map((m: any) => ({
    ...m,
    date: new Date(m.timestamp || -1)
  })).sort((a: any, b: any) => a.date.getTime() - b.date.getTime());

  return milestoneDates.map((milestone: any, index: number) => {
    const position = ((milestone.date.getTime() - paddedStart.getTime()) / 
                     (paddedEnd.getTime() - paddedStart.getTime())) * 100;
    
    return {
      name: milestone.name,
      timestamp: milestone.timestamp,
      description: milestone.description,
      position: Math.max(0, Math.min(100, position)),
      color: milestoneColors[index % milestoneColors.length]
    };
  });
};

/**
 * Processes milestones with alignment to pipeline columns for better visual correlation.
 * 
 * This function intelligently positions milestones within or near their corresponding pipeline columns
 * based on timing relationships. It performs the following operations:
 * 1. Sorts milestones chronologically to maintain temporal order
 * 2. Calculates the overall time range across all pipelines
 * 3. For each milestone, finds the best matching pipeline based on timing
 * 4. Positions the milestone within the matched pipeline's column or aligns it appropriately
 * 5. Applies visual offsets and constraints to ensure proper display
 * 
 * The algorithm prioritizes milestones that fall within pipeline execution windows,
 * and uses proximity-based matching for milestones outside any pipeline timeframe.
 * 
 * @param milestones - Array of milestone objects containing timestamp, name, description, and metadata
 * @param pipelines - Array of pipeline objects with startTime, endTime, and other execution details
 * @returns Array of processed milestones with calculated positions (0-100%), assigned colors, and metadata
 * ```
 */
export const processMilestonesWithPipelineAlignment = (
  milestones: Milestone[], 
  pipelines: Pipeline[]
): ProcessedMilestone[] => {
  if (!pipelines.length || !milestones.length) {
    return [];
  }

  // Sort milestones chronologically first to maintain order
  const milestoneWithDates = milestones.map((m: any) => ({
    ...m,
    date: new Date(m.timestamp || -1)
  })).sort((a: any, b: any) => a.date.getTime() - b.date.getTime());

  // Calculate the overall time range of all pipelines
  const {allPipelineStartTimes, allPipelineEndTimes} = getPipelineStartAndEndTimes(pipelines);

  if (allPipelineStartTimes.length === 0 || allPipelineEndTimes.length === 0) {
    // Fallback to original processing if no pipeline times available
    return fallbackStrategyForMilestoneProcessing(milestoneWithDates);
  }

  const overallStartTime = Math.min(...allPipelineStartTimes);
  const overallEndTime = Math.max(...allPipelineEndTimes);
  const overallDuration = overallEndTime - overallStartTime;

  return milestoneWithDates.map((milestone: any, index: number) => {
    const milestoneTime = milestone.date.getTime();
    // Calculate position based on the overall timeline of all pipelines
    let position = 0;
    if (overallDuration > 0) {
      const targetPipelineIndex = findPipelineMatchForMilestone(pipelines, milestoneTime);
      position = calculateMilestonePosition(targetPipelineIndex, pipelines, milestoneTime, overallStartTime, overallDuration);
    }
    
    return {
      name: milestone.name,
      timestamp: milestone.timestamp,
      description: milestone.description,
      position: Math.max(0, Math.min(100, position)),
      color: milestoneColors[index % milestoneColors.length],
      metadata: milestone.metadata
    };
  });
};

/**
 * Extracts and processes start and end times from all pipelines.
 * 
 * This utility function filters out pipelines without valid timing information
 * and converts Date objects to millisecond timestamps for easier mathematical operations.
 * It's used to establish the overall temporal boundaries for milestone positioning.
 * 
 * @param pipelines - Array of pipeline objects that may contain startTime and endTime properties
 * @returns Object containing arrays of start and end times in milliseconds
 * @returns {number[]} allPipelineStartTimes - Array of pipeline start times as millisecond timestamps
 * @returns {number[]} allPipelineEndTimes - Array of pipeline end times as millisecond timestamps
 * ```
 */
const getPipelineStartAndEndTimes = (pipelines: Pipeline[]) => {
  const allPipelineStartTimes = pipelines
    .filter(p => p.startTime)
    .map(p => p.startTime!.getTime());
  const allPipelineEndTimes = pipelines
    .filter(p => p.endTime)
    .map(p => p.endTime!.getTime());
  
  return {allPipelineStartTimes, allPipelineEndTimes}
}

/**
 * Finds the most appropriate pipeline to associate with a given milestone based on timing.
 * 
 * This function implements a two-phase matching algorithm:
 * 1. **Exact Match Phase**: Checks if the milestone timestamp falls within any pipeline's execution window
 * 2. **Proximity Match Phase**: If no exact match is found, selects the pipeline with the closest start time
 * 
 * The algorithm prioritizes temporal containment over proximity, ensuring that milestones
 * occurring during pipeline execution are always matched to the correct pipeline.
 * 
 * @param pipelines - Array of pipeline objects with startTime and endTime properties
 * @param milestoneTime - Milestone timestamp in milliseconds since epoch
 * @returns Index of the best matching pipeline, or -1 if no suitable match is found
 * ```
 */
const findPipelineMatchForMilestone = (pipelines: Pipeline[], milestoneTime: number) => {
  let targetPipelineIndex = -1;
  let bestMatch = Infinity;
  for (let pipelineIndex = 0; pipelineIndex < pipelines.length; pipelineIndex++) {
        const pipeline = pipelines[pipelineIndex];
        
        if (pipeline.startTime && pipeline.endTime) {
          const pipelineStart = pipeline.startTime.getTime();
          const pipelineEnd = pipeline.endTime.getTime();
          
          // Check if milestone falls within pipeline timeframe
          if (milestoneTime >= pipelineStart && milestoneTime <= pipelineEnd) {
            targetPipelineIndex = pipelineIndex;
            break;
          }
          
          // If not within any pipeline, find the closest pipeline start time
          const distanceToStart = Math.abs(milestoneTime - pipelineStart);
          
          if (distanceToStart < bestMatch) {
            bestMatch = distanceToStart;
            targetPipelineIndex = pipelineIndex;
          }
        }
      }
  return targetPipelineIndex
}

/**
 * Calculates the precise horizontal position for a milestone within the timeline visualization.
 * 
 * This function implements a sophisticated positioning algorithm that handles multiple scenarios:
 * 
 * **Scenario 1: Milestone within Pipeline Execution Window**
 * - Calculates proportional position within the pipeline's column
 * - Applies visual offset based on milestone timing (early vs late in pipeline)
 * - Uses LAYOUT_CONSTANTS.MILESTONE_POSITION_OFFSET_SMALL for fine-tuning
 * 
 * **Scenario 2: Milestone outside Pipeline Execution Window**
 * - Aligns milestone to pipeline start or end based on temporal proximity
 * - Ensures visual association with the closest pipeline boundary
 * 
 * **Scenario 3: No Pipeline Match Found**
 * - Falls back to proportional positioning across the entire timeline
 * - Maintains temporal accuracy when pipeline alignment isn't possible
 * 
 * @param targetPipelineIndex - Index of the matched pipeline (-1 if no match found)
 * @param pipelines - Array of all pipeline objects for width calculations
 * @param milestoneTime - Milestone timestamp in milliseconds since epoch
 * @param overallStartTime - Earliest start time across all pipelines (milliseconds)
 * @param overallDuration - Total duration spanning all pipelines (milliseconds)
 * @returns Calculated position as a percentage (0-100) of the total timeline width
 * ```
 */
const calculateMilestonePosition = (targetPipelineIndex: number, pipelines: Pipeline[], milestoneTime: number, overallStartTime: number, overallDuration: number) => {
  let position = 0;
  if (targetPipelineIndex >= 0) {
    const pipeline = pipelines[targetPipelineIndex];
    const pipelineWidth = 100 / pipelines.length;
    const pipelineStart = pipeline.startTime!.getTime();
    const pipelineEnd = pipeline.endTime!.getTime();
    const pipelineDuration = pipelineEnd - pipelineStart;
    
    // Position milestone within the pipeline column based on its timing
    if (pipelineDuration > 0 && milestoneTime >= pipelineStart && milestoneTime <= pipelineEnd) {
      // Milestone is within pipeline timeframe - position it proportionally within the column
      const relativePosition = (milestoneTime - pipelineStart) / pipelineDuration;
      const columnStart = targetPipelineIndex * pipelineWidth;
      position = columnStart + (relativePosition * pipelineWidth);
      // Add an offset based on milestone timing within the pipeline
      position = (milestoneTime - pipelineStart) > (pipelineEnd - milestoneTime)
        ? position - (pipelineWidth * LAYOUT_CONSTANTS.MILESTONE_POSITION_OFFSET_SMALL) 
        : position + (pipelineWidth * LAYOUT_CONSTANTS.MILESTONE_POSITION_OFFSET_SMALL);
    } else {
      // Milestone is outside pipeline timeframe - align it to the pipeline start or end depending upon proximity
      position = Math.abs(milestoneTime - pipelineStart) > Math.abs(pipelineEnd - milestoneTime) ?
      ((targetPipelineIndex+1) * pipelineWidth) : (targetPipelineIndex * pipelineWidth);
    }
  } else {
    // Fallback: position based on overall timeline
    position = ((milestoneTime - overallStartTime) / overallDuration) * 100;
  }
  
  return position;
}

/**
 * Provides a fallback strategy for milestone processing when pipeline alignment is not possible.
 * 
 * This function is used as a safety mechanism when:
 * - No pipelines are available for alignment
 * - Pipeline timing information is incomplete or invalid
 * - The primary alignment algorithm encounters errors
 * 
 * The fallback strategy distributes milestones evenly across the timeline based on their
 * chronological order, ensuring that the visualization remains functional even when
 * sophisticated pipeline alignment cannot be performed.
 * 
 * **Algorithm Details:**
 * - Maintains chronological order of milestones
 * - Distributes positions evenly from 0% to 100% of timeline width
 * - Handles edge cases (single milestone, empty arrays)
 * - Assigns colors cyclically from the predefined milestone color palette
 * 
 * @param milestoneWithDates - Array of milestone objects enhanced with parsed Date objects
 * @returns Array of processed milestones with evenly distributed positions and assigned colors
 */
const fallbackStrategyForMilestoneProcessing = (milestoneWithDates: Array<Milestone & {data: Date}>) => {
  return milestoneWithDates.map((milestone: Milestone & {data: Date}, index: number) => ({
      name: milestone.name,
      timestamp: milestone.timestamp,
      description: milestone.description,
      position: (index / Math.max(1, milestoneWithDates.length - 1)) * 100,
      color: milestoneColors[index % milestoneColors.length]
    }));
}
